/**
 * CrOptimizationRuleAutoActionOnCrBelowTargetPage — CRUD list + evaluate panel for
 * `cr-optimization-rule-auto-action-on-cr-below-target`.
 * Brand green (emerald) palette only.
 */
import { useState, type FormEvent } from "react";
import {
	useCrOptimizationRuleAutoActionOnCrBelowTargetCreate,
	useCrOptimizationRuleAutoActionOnCrBelowTargetEvaluate,
	useCrOptimizationRuleAutoActionOnCrBelowTargetList,
} from "./hooks";
import type { CrRuleAction } from "./api";

const CARD = "rounded-[8px] border border-zinc-200 bg-white p-5 shadow-sm";
const LABEL = "text-xs font-medium uppercase tracking-wider text-zinc-500";
const INPUT =
	"w-full rounded-[6px] border border-zinc-300 bg-white px-3 py-2 text-sm text-zinc-900 focus:border-green-600 focus:outline-none focus:ring-2 focus:ring-green-600/20";
const BTN =
	"rounded-[6px] border border-green-600 bg-green-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-green-700 disabled:cursor-not-allowed disabled:opacity-60";
const BADGE =
	"inline-flex items-center rounded-[6px] border border-green-200 bg-green-50 px-2 py-0.5 text-xs font-medium text-green-700";

const ACTIONS: readonly CrRuleAction[] = ["pause", "reduce_cap", "alert"] as const;

export function CrOptimizationRuleAutoActionOnCrBelowTargetPage() {
	const listQuery = useCrOptimizationRuleAutoActionOnCrBelowTargetList();
	const createMut = useCrOptimizationRuleAutoActionOnCrBelowTargetCreate();
	const evalMut = useCrOptimizationRuleAutoActionOnCrBelowTargetEvaluate();

	const [ruleKey, setRuleKey] = useState("");
	const [scope, setScope] = useState("");
	const [targetCr, setTargetCr] = useState("0.05");
	const [minClicks, setMinClicks] = useState("100");
	const [action, setAction] = useState<CrRuleAction>("pause");

	const [evalKey, setEvalKey] = useState("");
	const [evalClicks, setEvalClicks] = useState("");
	const [evalConversions, setEvalConversions] = useState("");

	const handleCreate = (e: FormEvent) => {
 e.preventDefault();
 const target = Number.parseFloat(targetCr);
 const clicks = Number.parseInt(minClicks, 10);
 if (!ruleKey.trim() || !scope.trim()) return;
 if (!Number.isFinite(target) || target < 0 || target > 1) return;
 if (!Number.isFinite(clicks) || clicks < 0) return;
 createMut.mutate(
 {
 rule_key: ruleKey.trim(),
 scope: scope.trim(),
 target_cr: target,
 min_clicks: clicks,
 action,
 },
 {
 onSuccess: () => {
 setRuleKey("");
 setScope("");
 },
 },
 );
	};

	const handleEvaluate = (e: FormEvent) => {
 e.preventDefault();
 const clicks = Number.parseInt(evalClicks, 10);
 const conversions = Number.parseInt(evalConversions, 10);
 if (!evalKey.trim()) return;
 if (!Number.isFinite(clicks) || clicks < 0) return;
 if (!Number.isFinite(conversions) || conversions < 0) return;
 evalMut.mutate({
 rule_key: evalKey.trim(),
 clicks,
 conversions,
 });
	};

	const items = listQuery.data?.items ?? [];
	const evalData = evalMut.data;

	return (
 <main
 data-testid="cr-optimization-rule-auto-action-on-cr-below-target-page"
 className="mx-auto flex w-full max-w-5xl flex-col gap-8 px-6 py-10"
 >
 <header className="flex flex-col gap-2">
 <p className="text-xs font-semibold uppercase tracking-wider text-green-700">
 cr-optimization-rule-auto-action-on-cr-below-target
 </p>
 <h1 className="text-green-700 font-semibold text-3xl font-semibold">
 CR Optimization Rules
 </h1>
 <p className="text-sm text-zinc-600">
 Auto action (pause, reduce cap, alert) when conversion rate falls below target for a scope with enough sample.
 </p>
 </header>

 <section className={CARD} aria-label="Create rule">
 <h2 className="text-green-700 font-semibold text-base font-semibold text-zinc-900">Create rule</h2>
 <form
 onSubmit={handleCreate}
 className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2"
 data-testid="cr-optimization-rule-auto-action-on-cr-below-target-create-form"
 >
 <label className="flex flex-col gap-1">
 <span className={LABEL}>Rule key</span>
 <input
 type="text"
 className={INPUT}
 placeholder="e.g. us-offer-cr-floor"
 value={ruleKey}
 onChange={(e) => setRuleKey(e.currentTarget.value)}
 disabled={createMut.isPending}
 data-testid="cr-optimization-rule-auto-action-on-cr-below-target-rule-key-input"
 />
 </label>
 <label className="flex flex-col gap-1">
 <span className={LABEL}>Scope</span>
 <input
 type="text"
 className={INPUT}
 placeholder="e.g. offer:1234"
 value={scope}
 onChange={(e) => setScope(e.currentTarget.value)}
 disabled={createMut.isPending}
 data-testid="cr-optimization-rule-auto-action-on-cr-below-target-scope-input"
 />
 </label>
 <label className="flex flex-col gap-1">
 <span className={LABEL}>Target CR (0-1)</span>
 <input
 type="number"
 step="0.001"
 min="0"
 max="1"
 className={INPUT}
 value={targetCr}
 onChange={(e) => setTargetCr(e.currentTarget.value)}
 disabled={createMut.isPending}
 data-testid="cr-optimization-rule-auto-action-on-cr-below-target-target-cr-input"
 />
 </label>
 <label className="flex flex-col gap-1">
 <span className={LABEL}>Min clicks</span>
 <input
 type="number"
 step="1"
 min="0"
 className={INPUT}
 value={minClicks}
 onChange={(e) => setMinClicks(e.currentTarget.value)}
 disabled={createMut.isPending}
 data-testid="cr-optimization-rule-auto-action-on-cr-below-target-min-clicks-input"
 />
 </label>
 <label className="flex flex-col gap-1 sm:col-span-2">
 <span className={LABEL}>Action when triggered</span>
 <select
 className={INPUT}
 value={action}
 onChange={(e) => setAction(e.currentTarget.value as CrRuleAction)}
 disabled={createMut.isPending}
 data-testid="cr-optimization-rule-auto-action-on-cr-below-target-action-select"
 >
 {ACTIONS.map((a) => (
 <option key={a} value={a}>
 {a}
 </option>
 ))}
 </select>
 </label>
 <div className="sm:col-span-2">
 <button
 type="submit"
 className={BTN}
 disabled={createMut.isPending || !ruleKey.trim() || !scope.trim()}
 data-testid="cr-optimization-rule-auto-action-on-cr-below-target-submit"
 >
 {createMut.isPending ? "Saving." : "Create rule"}
 </button>
 </div>
 </form>
 {createMut.error ? (
 <p className="mt-2 text-xs text-red-600" role="alert">
 {createMut.error.message}
 </p>
 ) : null}
 </section>

 <section className={CARD} aria-label="Rule list">
 <h2 className="text-green-700 font-semibold text-base font-semibold text-zinc-900">Rules</h2>
 {listQuery.isLoading ? (
 <p
 className="mt-3 text-sm text-zinc-500"
 data-testid="cr-optimization-rule-auto-action-on-cr-below-target-loading"
 >
 Loading.
 </p>
 ) : listQuery.error ? (
 <p className="mt-3 text-sm text-red-600" role="alert">
 {listQuery.error.message}
 </p>
 ) : items.length === 0 ? (
 <p
 className="mt-3 text-sm text-zinc-500"
 data-testid="cr-optimization-rule-auto-action-on-cr-below-target-empty"
 >
 No rules yet.
 </p>
 ) : (
 <ul
 className="mt-3 divide-y divide-zinc-200"
 data-testid="cr-optimization-rule-auto-action-on-cr-below-target-list"
 >
 {items.map((row) => (
 <li
 key={row.id}
 className="flex flex-col gap-1 py-3 text-sm sm:flex-row sm:items-center sm:justify-between"
 data-testid={`cr-optimization-rule-auto-action-on-cr-below-target-row-${row.id}`}
 >
 <div className="flex flex-col gap-0.5">
 <span className="font-medium text-zinc-900">{row.rule_key}</span>
 <span className="text-xs text-zinc-500">
 scope: <span className="font-mono">{row.scope}</span>
 </span>
 </div>
 <div className="flex flex-wrap items-center gap-2">
 <span className={BADGE}>target {row.target_cr}</span>
 <span className={BADGE}>min {row.min_clicks} clicks</span>
 <span className={BADGE}>{row.action}</span>
 </div>
 </li>
 ))}
 </ul>
 )}
 </section>

 <section className={CARD} aria-label="Evaluate rule">
 <h2 className="text-green-700 font-semibold text-base font-semibold text-zinc-900">Evaluate rule</h2>
 <p className="mt-1 text-sm text-zinc-600">
 Dry-run an observation against a stored rule to check whether the action would trigger.
 </p>
 <form
 onSubmit={handleEvaluate}
 className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-3"
 data-testid="cr-optimization-rule-auto-action-on-cr-below-target-eval-form"
 >
 <label className="flex flex-col gap-1 sm:col-span-3">
 <span className={LABEL}>Rule key</span>
 <input
 type="text"
 className={INPUT}
 value={evalKey}
 onChange={(e) => setEvalKey(e.currentTarget.value)}
 disabled={evalMut.isPending}
 data-testid="cr-optimization-rule-auto-action-on-cr-below-target-eval-key-input"
 />
 </label>
 <label className="flex flex-col gap-1">
 <span className={LABEL}>Clicks</span>
 <input
 type="number"
 step="1"
 min="0"
 className={INPUT}
 value={evalClicks}
 onChange={(e) => setEvalClicks(e.currentTarget.value)}
 disabled={evalMut.isPending}
 data-testid="cr-optimization-rule-auto-action-on-cr-below-target-eval-clicks-input"
 />
 </label>
 <label className="flex flex-col gap-1">
 <span className={LABEL}>Conversions</span>
 <input
 type="number"
 step="1"
 min="0"
 className={INPUT}
 value={evalConversions}
 onChange={(e) => setEvalConversions(e.currentTarget.value)}
 disabled={evalMut.isPending}
 data-testid="cr-optimization-rule-auto-action-on-cr-below-target-eval-conversions-input"
 />
 </label>
 <div className="sm:col-span-1 sm:self-end">
 <button
 type="submit"
 className={BTN}
 disabled={evalMut.isPending || !evalKey.trim()}
 data-testid="cr-optimization-rule-auto-action-on-cr-below-target-eval-submit"
 >
 {evalMut.isPending ? "Evaluating." : "Evaluate"}
 </button>
 </div>
 </form>
 {evalMut.error ? (
 <p className="mt-2 text-xs text-red-600" role="alert">
 {evalMut.error.message}
 </p>
 ) : null}
 {evalData ? (
 <div
 className="mt-4 rounded-[6px] border border-green-200 bg-green-50/70 p-3 text-sm"
 data-testid="cr-optimization-rule-auto-action-on-cr-below-target-eval-result"
 >
 <div className="flex flex-wrap items-center gap-2">
 <span className={BADGE}>cr {evalData.cr.toFixed(4)}</span>
 <span className={BADGE}>target {evalData.target_cr}</span>
 <span className={BADGE}>
 {evalData.observed_sample.enough_sample ? "enough sample" : "insufficient sample"}
 </span>
 <span className={BADGE}>
 {evalData.should_trigger ? "would trigger" : "no trigger"}
 </span>
 {evalData.action_when_triggered ? (
 <span className={BADGE}>action: {evalData.action_when_triggered}</span>
 ) : null}
 </div>
 </div>
 ) : null}
 </section>
 </main>
	);
}

export default CrOptimizationRuleAutoActionOnCrBelowTargetPage;
